import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Container,
  Table,
  Card,
  Badge,
  Button,
  Form,
} from "react-bootstrap";
import {
  FaSearch,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";

import { smsAPI } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import { toast } from "react-toastify";

import "../styles/InboundSMSPage.css";

const PAGE_SIZE = 100;

const InboundSMSPage = () => {
  const [sms, setSms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [replyFilter, setReplyFilter] = useState("all");
  const [replySort, setReplySort] = useState("latest");

  const [expandedId, setExpandedId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ------------------------------
  // Fetch SMS
  // ------------------------------
  const fetchSms = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: PAGE_SIZE,
        search,
        ordering: "-updated_at",
      };

      const res = await smsAPI.getInboundSMS(params);

      setSms(res.data.results || []);
      setCount(res.data.count || 0);
      setExpandedId(null);
    } catch {
      toast.error("Failed to load inbound SMS");
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    setLoading(true);
    fetchSms();
  }, [fetchSms]);

  const totalPages = Math.ceil(count / PAGE_SIZE);

  const hasCustomerReply = (transcript) => {
    if (!transcript) return false;

    return /(?:^|\n)\s*customer\s*:/i.test(transcript);
  };

  const displayedSms = useMemo(() => {
    let list = [...sms];

    if (replyFilter === "with_reply") {
      list = list.filter((msg) => hasCustomerReply(msg.transcript));
    } else if (replyFilter === "without_reply") {
      list = list.filter((msg) => !hasCustomerReply(msg.transcript));
    }

    list.sort((a, b) => {
      const timeA = new Date(a.updated_at).getTime();
      const timeB = new Date(b.updated_at).getTime();

      if (replySort === "oldest") {
        return timeA - timeB;
      }

      if (replySort === "reply_first") {
        const replyA = hasCustomerReply(a.transcript) ? 1 : 0;
        const replyB = hasCustomerReply(b.transcript) ? 1 : 0;
        if (replyB !== replyA) return replyB - replyA;
        return timeB - timeA;
      }

      if (replySort === "no_reply_first") {
        const noReplyA = hasCustomerReply(a.transcript) ? 0 : 1;
        const noReplyB = hasCustomerReply(b.transcript) ? 0 : 1;
        if (noReplyB !== noReplyA) return noReplyB - noReplyA;
        return timeB - timeA;
      }

      return timeB - timeA;
    });

    return list;
  }, [sms, replyFilter, replySort]);

  const renderTranscript = (transcript) => {
    if (!transcript) return null;

    const transcriptLines = transcript
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    return (
      <div className="mt-3 pt-3 border-top">
        <h6 className="text-primary mb-2">Transcript</h6>
        <div className="bg-light rounded p-3">
          {transcriptLines.map((line, index) => {
            const [speaker, ...messageParts] = line.split(":");
            const message = messageParts.join(":").trim();
            const normalizedSpeaker = speaker?.trim().toLowerCase();
            const isAgent = normalizedSpeaker === "agent";
            const isCustomer = normalizedSpeaker === "customer";

            return (
              <div key={`${line}-${index}`} className={`mb-2 ${index === transcriptLines.length - 1 ? "mb-0" : ""}`}>
                <span className={`fw-semibold ${isAgent ? "text-success" : isCustomer ? "text-warning-emphasis" : "text-primary"}`}>
                  {speaker || "Unknown"}:
                </span>{" "}
                <span className="text-body">{message || line}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) return <LoadingSpinner text="Loading inbound SMS..." />;

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <Container fluid className="page-container">
      {/* HEADER */}
      <div className="mb-4">
        <div className={`d-flex align-items-center justify-content-between mb-2 ${isMobile ? 'flex-column align-items-start' : ''}`}>
          <div className={`d-flex align-items-center ${isMobile ? 'mb-2' : ''}`}>
            <h1 className="page-header-title mb-0 me-3">Inbound SMS</h1>
            <Badge bg="primary" className="fs-6">{count} SMS</Badge>
          </div>
        </div>
        <p className="page-header-subtitle text-muted mb-0">
          View incoming SMS messages with lead information
        </p>
      </div>

      {/* SEARCH */}
      <Card className="mb-4 shadow-sm">
        <Card.Body className={`p-${isMobile ? '2' : '3'}`}>
          <Form.Group>
            <Form.Label className="fw-semibold">Search SMS</Form.Label>
            <div className="position-relative">
              <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Form.Control
                placeholder="Search by name, phone, or message..."
                value={search}
                onChange={(e) => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                className="ps-5"
                size={isMobile ? "sm" : "md"}
              />
            </div>
          </Form.Group>

          <div className={`mt-3 d-grid ${isMobile ? "gap-2" : "gap-3"}`} style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
            <Form.Group>
              <Form.Label className="fw-semibold mb-1">Sort</Form.Label>
              <Form.Select
                value={replySort}
                onChange={(e) => setReplySort(e.target.value)}
                size={isMobile ? "sm" : "md"}
              >
                <option value="latest">Latest first</option>
                <option value="oldest">Oldest first</option>
                <option value="reply_first">Customer reply first</option>
                <option value="no_reply_first">No reply first</option>
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label className="fw-semibold mb-1">Reply Filter</Form.Label>
              <Form.Select
                value={replyFilter}
                onChange={(e) => setReplyFilter(e.target.value)}
                size={isMobile ? "sm" : "md"}
              >
                <option value="all">All messages</option>
                <option value="with_reply">With customer reply</option>
                <option value="without_reply">Without customer reply</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Card.Body>
      </Card>

      {/* CONTENT AREA */}
      <div className="position-relative">
        {loading && (
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
            <LoadingSpinner text="Loading SMS..." />
          </div>
        )}

        {/* =======================
            MOBILE – CARDS
        ======================= */}
        {isMobile ? (
          displayedSms.map((msg) => {
            const expanded = expandedId === msg.id;
            const replyHighlighted = hasCustomerReply(msg.transcript);

            return (
              <Card key={msg.id} className={`mb-3 shadow-sm hover-lift ${replyHighlighted ? "sms-card-has-reply" : ""}`}>
                <Card.Body
                  onClick={() => setExpandedId(expanded ? null : msg.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedId(expanded ? null : msg.id);
                    }
                  }}
                  className="p-3"
                  style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
                  tabIndex={0}
                  role="button"
                  aria-expanded={expanded}
                  aria-label={`SMS from ${msg.name || 'Unknown'} - ${expanded ? 'Collapse' : 'Expand'} details`}
                >
                  {/* Header */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="flex-grow-1 me-2">
                      <div className="fw-semibold text-truncate">{msg.name || "Unknown"}</div>
                      <div className="text-muted small text-truncate">{msg.phone_number}</div>
                    </div>
                    <div className="text-end d-flex flex-column align-items-end gap-1">
                      {replyHighlighted && (
                        <Badge bg="warning" text="dark" className="align-self-end">
                          Customer reply
                        </Badge>
                      )}
                      <small className="text-muted d-block">
                        {new Date(msg.updated_at).toLocaleDateString()}
                      </small>
                      <small className="text-muted">
                        {new Date(msg.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </small>
                    </div>
                  </div>

                  {/* Message Preview */}
                  <div className="mb-2">
                    <div className="text-break" style={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: expanded ? 'none' : 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: '1.4'
                    }}>
                      {msg.body}
                    </div>
                  </div>

                  {/* Expand/Collapse Indicator */}
                  <div className="text-center">
                    <small className="text-muted">
                      {expanded ? 'Tap to collapse' : 'Tap for details'}
                    </small>
                  </div>

                  {expanded && (
                    <div className="mt-3 pt-3 border-top">
                      <div className="row g-2">
                        <div className="col-6">
                          <strong className="small text-muted d-block">To</strong>
                          <div className="small font-monospace">{msg.to_number}</div>
                        </div>
                        <div className="col-6">
                          <strong className="small text-muted d-block">Address</strong>
                          <div className="small">{msg.address || "Not provided"}</div>
                        </div>
                      </div>
                      {renderTranscript(msg.transcript)}
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })
        ) : (
          /* =======================
              DESKTOP – TABLE
          ======================= */
          <Card className="shadow-sm">
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-0 fw-semibold">Sender</th>
                    <th className="border-0 fw-semibold">Mike</th>
                    <th className="border-0 fw-semibold">Message</th>
                    <th className="border-0 fw-semibold">Address</th>
                    <th className="border-0 fw-semibold">Updated At</th>
                    <th className="border-0 fw-semibold text-center">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedSms.map((msg) => (
                    <React.Fragment key={msg.id}>
                      <tr
                        className={`align-middle ${hasCustomerReply(msg.transcript) ? "sms-row-has-reply" : ""}`}
                        onClick={() =>
                          setExpandedId(expandedId === msg.id ? null : msg.id)
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedId(expandedId === msg.id ? null : msg.id);
                          }
                        }}
                        style={{ cursor: "pointer" }}
                        tabIndex={0}
                        role="button"
                        aria-expanded={expandedId === msg.id}
                        aria-label={`SMS from ${msg.name || 'Unknown'} - ${expandedId === msg.id ? 'Collapse' : 'Expand'} details`}
                      >
                        <td>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="fw-semibold">{msg.name || "Unknown"}</div>
                            {hasCustomerReply(msg.transcript) && (
                              <Badge bg="warning" text="dark">
                                Customer reply
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted small">{msg.from_number}</div>
                        </td>
                        <td className="font-monospace">{msg.to_number}</td>
                        <td className="text-truncate" style={{ maxWidth: "300px" }} title={msg.body}>
                          {msg.body}
                        </td>
                        <td>{msg.address || "Not provided"}</td>
                        <td className="small">
                          {new Date(msg.updated_at).toLocaleDateString()}
                          <br />
                          <span className="text-muted">
                            {new Date(msg.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>
                        <td className="text-center">
                          <Button variant="link" size="sm" className="p-0 text-decoration-none">
                            {expandedId === msg.id ? (
                              <FaChevronUp className="text-primary" />
                            ) : (
                              <FaChevronDown className="text-muted" />
                            )}
                          </Button>
                        </td>
                      </tr>

                      {expandedId === msg.id && (
                        <tr className="bg-light">
                          <td colSpan={6} className="border-0">
                            <Card className="border-0 shadow-none bg-transparent">
                              <Card.Body className="p-3">
                                <div className="row">
                                  <div className="col-md-12">
                                    <h6 className="text-primary mb-2">Full Message</h6>
                                    <p className="mb-3">{msg.body}</p>
                                  </div>
                                </div>
                                {renderTranscript(msg.transcript)}
                              </Card.Body>
                            </Card>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        )}

        {/* PAGINATION */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <div className={`pagination-controls d-flex align-items-center ${isMobile ? 'flex-column gap-2' : 'gap-3'}`}>
              <Button
                variant="outline-primary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                size={isMobile ? "sm" : "md"}
              >
                Previous
              </Button>
              <span className={`text-muted ${isMobile ? 'text-center' : ''}`}>
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline-primary"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                size={isMobile ? "sm" : "md"}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

export default InboundSMSPage;